import { Slider as SliderPrimitive } from "@base-ui/react/slider";

import { cn } from "@loot-os/ui/lib/utils";

type SliderProps = Omit<
  SliderPrimitive.Root.Props<number>,
  "value" | "defaultValue" | "onValueChange" | "onValueCommitted"
> & {
  value: number;
  onValueChange?: (value: number) => void;
  /** `reason` says what committed it: `"keyboard"` for each arrow press, `"drag"` on release. */
  onValueCommitted?: (value: number, reason: SliderPrimitive.Root.CommitEventReason) => void;
  /** Names the thumb (Base UI puts the label on the thumb's input, not the root). */
  "aria-label"?: string;
  getAriaValueText?: (formattedValue: string, value: number) => string;
};

/** A single-thumb slider; the value is a plain number rather than Base UI's number-or-array. */
function Slider({
  className,
  value,
  onValueChange,
  onValueCommitted,
  "aria-label": ariaLabel,
  getAriaValueText,
  ...props
}: SliderProps) {
  return (
    <SliderPrimitive.Root
      data-slot="slider"
      value={value}
      onValueChange={(next) => onValueChange?.(Array.isArray(next) ? (next[0] ?? 0) : next)}
      onValueCommitted={(next, details) =>
        onValueCommitted?.(Array.isArray(next) ? (next[0] ?? 0) : next, details.reason)
      }
      className={cn("data-horizontal:w-full", className)}
      {...props}
    >
      <SliderPrimitive.Control className="relative flex w-full touch-none items-center py-2 select-none data-disabled:opacity-50">
        <SliderPrimitive.Track
          data-slot="slider-track"
          className="relative h-1 w-full grow rounded-full bg-muted select-none"
        >
          <SliderPrimitive.Indicator
            data-slot="slider-range"
            className="rounded-full bg-primary select-none"
          />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          aria-label={ariaLabel}
          getAriaValueText={getAriaValueText}
          className="block size-3 shrink-0 rounded-full border border-ring bg-white ring-ring/50 transition-[color,box-shadow] outline-none select-none hover:ring-3 focus-visible:ring-3 active:ring-3 disabled:pointer-events-none disabled:opacity-50"
        />
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  );
}

export { Slider };
